import PlayPositions from "@Enums/PlayPositions";
import FieldPositions from "@Enums/SimplifiedPlayPositions";

export enum FormationPositionCategory {
  GOALKEEPER = "goalkeeper",
  DEFENSE = "defense",
  MIDFIELD = "midfield",
  FORWARD = "forward",
}

type PositionItem = {
  id: PlayPositions;
  nameKey: string;
  fullnameKey: string;
  position: { top: string; left: string };
};

export type PositionSelectorConfig = {
  positions: Record<FormationPositionCategory, PositionItem[]>;
  sections: { id: FieldPositions; labelKey: string; positions: PlayPositions[] }[];
};

export const DEFAULT_POSITION_GROUPS: PositionSelectorConfig["positions"] = {
  goalkeeper: [
    {
      id: PlayPositions.GOALKEEPER,
      nameKey: "position.short.goalkeeper",
      fullnameKey: "position.goalkeeper",
      position: { top: "50%", left: "8%" },
    },
  ],
  defense: [
    {
      id: PlayPositions.LEFT_FULLBACK,
      nameKey: "position.short.left_fullback",
      fullnameKey: "position.left_fullback",
      position: { top: "15%", left: "35%" },
    },
    {
      id: PlayPositions.CENTER_BACK,
      nameKey: "position.short.center_back",
      fullnameKey: "position.center_back",
      position: { top: "38.33%", left: "32.5%" },
    },
    {
      id: PlayPositions.CENTER_BACK,
      nameKey: "position.short.center_back",
      fullnameKey: "position.center_back",
      position: { top: "61.66%", left: "32.5%" },
    },
    {
      id: PlayPositions.RIGHT_FULLBACK,
      nameKey: "position.short.right_fullback",
      fullnameKey: "position.right_fullback",
      position: { top: "85%", left: "35%" },
    },
  ],
  midfield: [
    {
      id: PlayPositions.DEFENSIVE_MIDFIELDER,
      nameKey: "position.short.defensive_midfielder",
      fullnameKey: "position.defensive_midfielder",
      position: { top: "50%", left: "55%" },
    },
    {
      id: PlayPositions.CENTER_MIDFIELDER,
      nameKey: "position.short.center_midfielder",
      fullnameKey: "position.center_midfielder",
      position: { top: "30%", left: "62.5%" },
    },
    {
      id: PlayPositions.ATTACKING_MIDFIELDER,
      nameKey: "position.short.attacking_midfielder",
      fullnameKey: "position.attacking_midfielder",
      position: { top: "70%", left: "62.5%" },
    },
  ],
  forward: [
    {
      id: PlayPositions.LEFT_WINGER,
      nameKey: "position.short.left_winger",
      fullnameKey: "position.left_winger",
      position: { top: "17.5%", left: "82.5%" },
    },
    {
      id: PlayPositions.STRIKER,
      nameKey: "position.short.striker",
      fullnameKey: "position.striker",
      position: { top: "50%", left: "87.5%" },
    },
    {
      id: PlayPositions.RIGHT_WINGER,
      nameKey: "position.short.right_winger",
      fullnameKey: "position.right_winger",
      position: { top: "82.5%", left: "82.5%" },
    },
  ],
};

export const DEFAULT_POSITION_SECTIONS: PositionSelectorConfig["sections"] = [
  { id: FieldPositions.GOALKEEPER, labelKey: "position.goalkeeper", positions: [PlayPositions.GOALKEEPER] },
  {
    id: FieldPositions.DEFENSIVE,
    labelKey: "position.defensive",
    positions: [PlayPositions.LEFT_FULLBACK, PlayPositions.CENTER_BACK, PlayPositions.RIGHT_FULLBACK],
  },
  {
    id: FieldPositions.MIDFIELD,
    labelKey: "position.midfield",
    positions: [PlayPositions.DEFENSIVE_MIDFIELDER, PlayPositions.CENTER_MIDFIELDER, PlayPositions.ATTACKING_MIDFIELDER],
  },
  {
    id: FieldPositions.FORWARD,
    labelKey: "position.forward",
    positions: [PlayPositions.LEFT_WINGER, PlayPositions.STRIKER, PlayPositions.RIGHT_WINGER],
  },
] as const;

export const DEFAULT_POSITION_SELECTOR_CONFIG: PositionSelectorConfig = {
  positions: DEFAULT_POSITION_GROUPS,
  sections: DEFAULT_POSITION_SECTIONS,
};

export const MATCHPLAY_POSITION_GROUPS: PositionSelectorConfig["positions"] = {
  goalkeeper: DEFAULT_POSITION_GROUPS.goalkeeper,
  defense: [
    { id: PlayPositions.LEFT_FULLBACK, nameKey: "position.short.left_fullback", fullnameKey: "position.left_fullback", position: { top: "15%", left: "35%" } },
    { id: PlayPositions.LEFT_CENTER_BACK, nameKey: "position.short.left_center_back", fullnameKey: "position.left_center_back", position: { top: "38.33%", left: "32.5%" } },
    { id: PlayPositions.RIGHT_CENTER_BACK, nameKey: "position.short.right_center_back", fullnameKey: "position.right_center_back", position: { top: "61.66%", left: "32.5%" } },
    { id: PlayPositions.RIGHT_FULLBACK, nameKey: "position.short.right_fullback", fullnameKey: "position.right_fullback", position: { top: "85%", left: "35%" } },
  ],
  midfield: [
    { id: PlayPositions.DEFENSIVE_MIDFIELDER, nameKey: "position.short.defensive_midfielder", fullnameKey: "position.defensive_midfielder", position: { top: "50%", left: "55%" } },
    {
      id: PlayPositions.LEFT_CENTER_MIDFIELDER,
      nameKey: "position.short.left_center_midfielder",
      fullnameKey: "position.left_center_midfielder",
      position: { top: "30%", left: "62.5%" },
    },
    {
      id: PlayPositions.RIGHT_CENTER_MIDFIELDER,
      nameKey: "position.short.right_center_midfielder",
      fullnameKey: "position.right_center_midfielder",
      position: { top: "70%", left: "62.5%" },
    },
  ],
  forward: DEFAULT_POSITION_GROUPS.forward,
};

export const MATCHPLAY_POSITION_SELECTOR_CONFIG: PositionSelectorConfig = {
  positions: MATCHPLAY_POSITION_GROUPS,
  sections: [
    { id: FieldPositions.GOALKEEPER, labelKey: "position.goalkeeper", positions: [PlayPositions.GOALKEEPER] },
    {
      id: FieldPositions.DEFENSIVE,
      labelKey: "position.defensive",
      positions: [PlayPositions.LEFT_FULLBACK, PlayPositions.LEFT_CENTER_BACK, PlayPositions.RIGHT_CENTER_BACK, PlayPositions.RIGHT_FULLBACK],
    },
    {
      id: FieldPositions.MIDFIELD,
      labelKey: "position.midfield",
      positions: [PlayPositions.DEFENSIVE_MIDFIELDER, PlayPositions.LEFT_CENTER_MIDFIELDER, PlayPositions.RIGHT_CENTER_MIDFIELDER],
    },
    {
      id: FieldPositions.FORWARD,
      labelKey: "position.forward",
      positions: [PlayPositions.LEFT_WINGER, PlayPositions.STRIKER, PlayPositions.RIGHT_WINGER],
    },
  ],
};

export const flattenPositionConfig = (config: PositionSelectorConfig["positions"]): PositionItem[] => [
  ...config.goalkeeper,
  ...config.defense,
  ...config.midfield,
  ...config.forward,
];

export default DEFAULT_POSITION_GROUPS;
